'use client';

import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_THEME, THEME_COOKIE_MAX_AGE } from './config';
import type { Theme } from './resolveTheme';

const THEME_CHANGE_EVENT = 'themechange';

function getThemeFromDocument(): Theme {
  if (typeof document === 'undefined') {
    return DEFAULT_THEME as Theme;
  }
  const fromAttr = document.documentElement.getAttribute('data-theme');
  if (fromAttr === 'light' || fromAttr === 'dark') {
    return fromAttr;
  }
  const fromCookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('theme='))
    ?.split('=')[1];
  if (fromCookie === 'light' || fromCookie === 'dark') {
    return fromCookie;
  }
  return (DEFAULT_THEME as Theme) ?? 'light';
}

/**
 * @param initialTheme SSR 에서 theme 쿠키로 확정한 초기 테마.
 *   server 와 client 의 첫 렌더가 동일 값을 쓰도록 seed 로 넘긴다.
 *   생략 시 server 는 DEFAULT_THEME, client 는 document 값을 읽는데,
 *   둘이 달라지면 헤더 data-theme 가 hydration mismatch 로 어긋난다.
 */
export function useTheme(
  initialTheme?: Theme,
): [Theme, (theme: Theme) => void] {
  const [theme, setThemeState] = useState<Theme>(
    () =>
      initialTheme ??
      (typeof document !== 'undefined'
        ? getThemeFromDocument()
        : (DEFAULT_THEME as Theme)),
  );

  useEffect(() => {
    setThemeState(getThemeFromDocument());
    const handler = () => {
      setThemeState(getThemeFromDocument());
    };
    window.addEventListener(THEME_CHANGE_EVENT, handler);
    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, handler);
    };
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    document.cookie = `theme=${newTheme}; path=/; max-age=${THEME_COOKIE_MAX_AGE};`;
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT));
  }, []);

  return [theme, setTheme];
}
